import React from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Pagination } from 'ats/components/pagination/Pagination';
import DrawerContainer from 'ats/components/drawer/DrawerContainer';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import FilterBar from 'ats/components/filter-bar/FilterBar';
import FilterDrawer from 'ats/components/filter-drawer/FilterDrawer';
import { buildAppliedFilterItems } from 'ats/components/filter-drawer/buildAppliedFilterItems';
import { ListStore } from 'shared/stores/ListStore';
import JobPositionProfile from './JobPositionProfile';
import { jobPositionStore } from './JobPositionStore';
import JobPositionsTable from './JobPositionsTable';
import { JobPositionCreate } from './components/JobPositionCreate';
import JobPositionCard from './components/JobPositionCard';
import { eventManager } from 'ats/utils/EventManager';
import moment from 'moment-timezone';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import ViewsDropdown from 'ats/components/views-dropdown/ViewsDropdown';
import { ListViewController } from 'ats/components/views-dropdown/ListViewController';
import { LIST_VIEW_SECTIONS } from 'ats/components/views-dropdown/listViewSections';
import { attachListProfileDrawerHandlers } from 'ats/utils/openEntityProfile';

@withRouter
@observer
export class JobPositions extends React.Component {
	defaultFilterValues = {
		statusFilter: {
			...jobPositionStore.getStatusByValue('open'),
			label: 'Status: Open'
		}
	};

	state = {
		activeJobPositionId: null,
		searchFilter: null,
		statusFilter: this.defaultFilterValues.statusFilter,
		clientFilter: [],
		createdOnFilter: null,
		urgencyFilter: null,
		priorityFilter: null,
		disabledFilter: null,
		ownerFilter: null,
		hiringManagerFilter: null,
		accountManagerFilter: null,
		timezoneFilter: null,
		visibleColumns: jobPositionStore.defaultTableColumnsOptions,
		mineOnly: false,
		jobIdFilter: []
	};

	constructor(props) {
		super(props);
		this.listStore = new ListStore(jobPositionStore, {
			entityName: 'Job Position',
			createModalTitle: 'New Job',
			createModalPosition: 'center',
			createModalSize: '2xl',
			createModal: (
				<JobPositionCreate handleSuccess={this.handleCreateSuccess} />
			)
		});
		this.listViewController = new ListViewController(
			LIST_VIEW_SECTIONS.JOBS,
			this
		);
		this.listStore.beginLoading();
	}

	async componentDidMount() {
		await this.listViewController.initialize();

		const jobPositionIdFromUrl = this.props.match?.params?.id;
		if (jobPositionIdFromUrl) {
			this.openProfileById(jobPositionIdFromUrl);
		}

		eventManager.on('taskChanged', this.handleTaskChanged);
	}

	componentWillUnmount() {
		this.listViewController.cancel();
		eventManager.off('taskChanged', this.handleTaskChanged);

		this.listStore.store.resetQuery();

		if (drawerStore.isOpen) {
			drawerStore.closeDrawer();
		}
	}

	componentDidUpdate(prevProps) {
		const prevId = prevProps.match?.params?.id;
		const nextId = this.props.match?.params?.id;

		if (prevId === nextId) return;

		if (nextId) {
			this.openProfileById(nextId);
		} else {
			if (
				drawerStore.isOpen &&
				drawerStore.drawerId === drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE
			) {
				drawerStore.closeDrawer();
			}
			this.setState({ activeJobPositionId: null });
		}
	}

	handleCreateSuccess = result => {
		this.listStore.search();
		if (result?.data?.id) {
			this.props.history.push(`${this.getBaseUrl()}/${result.data.id}`);
		}
	};

	handleTaskChanged = () => {
		this.listStore.search();
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	getPriority = value => {
		return jobPositionStore.priorityOptions.find(
			priority => priority.value === value
		);
	};

	getUrgency = value => {
		return jobPositionStore.urgencyOptions.find(
			urgency => urgency.value === value
		);
	};

	getBaseUrl = () => {
		const url = this.props.match?.url || '';
		const id = this.props.match?.params?.id;
		if (!id) return url;
		return url.replace(new RegExp(`/${id}$`), '');
	};

	openProfileById = jobPositionId => {
		this.setState({ activeJobPositionId: jobPositionId });

		const onClose = () => {
			this.setState({ activeJobPositionId: null });
			drawerStore.closeDrawer();
			this.props.history.push(this.getBaseUrl());
		};
		const onSuccess = () => this.listStore.search();

		if (
			attachListProfileDrawerHandlers({
				drawerId: drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				idKey: 'jobPositionId',
				entityId: jobPositionId,
				onClose,
				onSuccess
			})
		) {
			return;
		}

		drawerStore.openDrawer(
			{
				containerId: 'job-positions',
				showHeader: false,
				drawerId: drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				props: {
					jobPositionId,
					onSuccess,
					onClose
				}
			},
			JobPositionProfile
		);
	};

	openProfile = rowData => {
		const baseUrl = this.getBaseUrl();
		this.props.history.push(`${baseUrl}/${rowData.id}`);
		this.openProfileById(rowData.id);
	};

	getBarFilters = () => {
		return [
			{
				type: 'search',
				label: 'Search job title or number',
				name: 'search',
				onKeyPress: newValue => {
					this.setState({
						searchFilter: newValue
					});
				},
				onChange: newValue => {
					this.listStore.store.setQuery('title', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.searchFilter
			},
			{
				type: 'dropdown',
				label: 'Status',
				placeholder: 'Status: All',
				name: 'status',
				onChange: newValue => {
					this.setState({
						statusFilter: newValue
							? {
									...newValue,
									label: `Status: ${newValue.label}`
								}
							: null
					});
					this.listStore.store.setQuery('status', newValue?.value ?? null);
					this.handlePaginationChange(1);
				},
				value: this.state.statusFilter,
				options: [
					{ label: 'All', value: null },
					...jobPositionStore.statusOptions.map(status => ({
						...status,
						label: status.text
					}))
				]
			},
			{
				type: 'client-dropdown',
				name: 'client',
				label: 'Client',
				placeholder: 'Client: All',
				isClearable: true,
				isMulti: true,
				onChange: newValue => {
					this.setState({
						clientFilter: newValue || []
					});
					this.listStore.store.setQuery(
						'clientId',
						(newValue || []).map(c => c.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.clientFilter
			}
		];
	};

	getDrawerFilters = () => {
		return [
			{
				type: 'creatable-multi',
				label: 'Job Number',
				name: 'job-number',
				onChange: newValue => {
					this.setState(
						{
							jobIdFilter: newValue || []
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);

					const ids = (newValue || [])
						.map(option => parseInt(option.value, 10))
						.filter(id => !Number.isNaN(id));

					this.listStore.store.setQuery('id', ids.length ? ids : null);
					this.handlePaginationChange(1);
				},
				value: this.state.jobIdFilter
			},
			{
				type: 'calendar',
				label: 'Created On',
				placeholder: 'MM/DD/YYYY',
				name: 'created-on',
				isRange: true,
				onChange: dates => {
					this.setState({ createdOnFilter: dates }, () => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					});

					if (dates?.[0] && dates?.[1]) {
						this.listStore.store.setQuery('createdAt', [
							moment(dates[0]).startOf('day').toDate(),
							moment(dates[1]).endOf('day').toDate()
						]);
					} else {
						this.listStore.store.setQuery('createdAt', null);
					}
					this.handlePaginationChange(1);
				},
				value: this.state.createdOnFilter
			},
			{
				type: 'dropdown',
				label: 'Urgency',
				name: 'urgency',
				onChange: newValue => {
					this.setState(
						{
							urgencyFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery('urgency', newValue?.value ?? null);
					this.handlePaginationChange(1);
				},
				value: this.state.urgencyFilter,
				options: [
					{ label: 'All', value: null },
					...jobPositionStore.urgencyOptions.map(urgency => ({
						...urgency,
						label: urgency.text
					}))
				]
			},
			{
				type: 'checkbox-group',
				label: 'Priority',
				name: 'priority',
				allowMultiple: true,
				onChange: newValue => {
					this.setState(
						{
							priorityFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'priority',
						newValue.map(priority => priority.value)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.priorityFilter,
				options: [
					...jobPositionStore.priorityOptions.map(priority => ({
						...priority,
						label: priority.text
					}))
				]
			},
			{
				type: 'switch',
				label: 'Published',
				id: 'disabled-switch',
				onChange: newValue => {
					this.setState({ disabledFilter: !newValue }, () => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					});
					this.listStore.store.setQuery('disabled', !newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.disabledFilter
			},
			{
				type: 'user-dropdown',
				label: 'Owner',
				isMulti: true,
				role: NON_CANDIDATE_USER_ROLES,
				isClearable: true,
				onChange: newValue => {
					this.setState(
						{
							ownerFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'owner',
						newValue.map(o => o.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.ownerFilter
			},
			{
				type: 'client-contact-dropdown',
				label: 'Hiring Manager',
				isMulti: true,
				isClearable: true,
				onChange: newValue => {
					this.setState(
						{
							hiringManagerFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'hiringManager',
						newValue.map(h => h.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.hiringManagerFilter
			},
			{
				type: 'user-dropdown',
				label: 'Account Manager',
				isMulti: true,
				role: 'Account Manager',
				minSearchChars: 0,
				isClearable: true,
				onChange: newValue => {
					this.setState(
						{
							accountManagerFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'accountManager',
						newValue.map(h => h.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.accountManagerFilter
			},
			{
				type: 'timezone-dropdown',
				label: 'Time Zone',
				isMulti: true,
				isClearable: true,
				onChange: newValue => {
					this.setState(
						{
							timezoneFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'timezones',
						newValue.map(t => t.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.timezoneFilter
			}
		];
	};

	getAppliedFilters = () =>
		buildAppliedFilterItems([
			...this.getBarFilters(),
			...this.getDrawerFilters()
		]);

	hasUserAppliedFilters = () => {
		const filtersWithoutStatus = [
			...this.getBarFilters(),
			...this.getDrawerFilters()
		].filter(filter => filter.name !== 'status');
		const statusValue = this.state.statusFilter?.value ?? null;
		const defaultStatusValue =
			this.defaultFilterValues.statusFilter?.value ?? null;
		const hasNonDefaultStatus =
			statusValue !== null && statusValue !== defaultStatusValue;

		return (
			hasNonDefaultStatus ||
			buildAppliedFilterItems(filtersWithoutStatus).length > 0
		);
	};

	resetDrawerFilters = () => {
		this.setState(
			{
				searchFilter: '',
				statusFilter: null,
				clientFilter: [],
				createdOnFilter: null,
				urgencyFilter: null,
				priorityFilter: null,
				disabledFilter: null,
				ownerFilter: null,
				hiringManagerFilter: null,
				accountManagerFilter: null,
				timezoneFilter: null,
				mineOnly: false,
				jobIdFilter: []
			},
			() => {
				drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
			}
		);

		this.listStore.store.resetQuery();
		this.handlePaginationChange(1);
	};

	renderJobPositionsEmptyState = () => {
		if (this.state.mineOnly) {
			return (
				<EmptyState
					size="sm"
					icon={EMPTY_STATE_ICONS.allCaughtUp}
					iconAlt="All caught up"
					message="All caught up"
					detail="You’re viewing only your pending tasks. Want to see all data?"
					actionText="Turn off Me mode"
					onActionClick={() => {
						this.setState({ mineOnly: false });
						this.listStore.store.setQuery('mineOnly', null);
						this.handlePaginationChange(1);
					}}
				/>
			);
		}

		const hasFilters = this.hasUserAppliedFilters();

		return (
			<EmptyState
				icon={
					hasFilters ? EMPTY_STATE_ICONS.noResultsJobs : EMPTY_STATE_ICONS.job
				}
				iconAlt={hasFilters ? 'No matching jobs' : 'No jobs yet'}
				message={
					hasFilters ? 'No jobs match your search criteria.' : 'No jobs yet.'
				}
				detail={
					hasFilters
						? 'Try adjusting your filters or search terms to see more results.'
						: 'Create a job to get started.'
				}
				actionText={hasFilters ? 'Clear filters' : 'New Job'}
				onActionClick={
					hasFilters
						? this.resetDrawerFilters
						: () => this.listStore.handleCreate()
				}
			/>
		);
	};

	render() {
		const isProfileOpen =
			drawerStore.isOpen &&
			[
				drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				drawerStore.DRAWER_IDS.CLIENT_PROFILE
			].includes(drawerStore.drawerId);

		const isFilterDrawerOpen =
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.FILTER_DRAWER;

		return (
			<div className="JobPositions">
				<div className="header-container">
					<h2>Jobs</h2>
					<ViewsDropdown
						section={LIST_VIEW_SECTIONS.JOBS}
						controller={this.listViewController}
						viewStateKey={this.listViewController.getViewStateKey()}
						isModified={this.listViewController.isModified()}
					/>
					<FilterBar
						filters={this.getBarFilters()}
						appliedFilters={this.getAppliedFilters()}
						showMineToggle
						mineOn={this.state.mineOnly}
						onMineChange={nextValue => {
							this.setState({ mineOnly: nextValue });
							this.listStore.store.setQuery(
								'mineOnly',
								nextValue ? true : null
							);
							this.handlePaginationChange(1);
						}}
						onShowMore={() => {
							if (!isFilterDrawerOpen) {
								drawerStore.openDrawer(
									{
										containerId: 'job-positions',
										drawerId: drawerStore.DRAWER_IDS.FILTER_DRAWER,
										customHeader: (
											<>
												<button
													className="filter-drawer-back-btn"
													onClick={() => drawerStore.closeDrawer()}>
													<img
														src="/ats/icons/arrow-right-purple-icon.svg"
														alt=""
													/>
												</button>
												<h3>Filters</h3>
											</>
										),
										props: {
											filters: this.getDrawerFilters(),
											getAppliedFilters: this.getAppliedFilters,
											onReset: this.resetDrawerFilters
										}
									},
									FilterDrawer
								);
							} else {
								drawerStore.closeDrawer();
							}
						}}
					/>
					<button
						type="button"
						className="ats-button-primary"
						onClick={() => this.listStore.handleCreate()}>
						New Job
					</button>
				</div>

				<main>
					<div
						className={`job-positions-wrapper${
							isProfileOpen ? ' is-profile-open' : ''
						}`}>
						{(() => {
							const showMineEmpty =
								this.state.mineOnly &&
								!this.listStore.loading &&
								this.listStore.data.length === 0;

							const turnOffMine = () => {
								this.setState({ mineOnly: false });
								this.listStore.store.setQuery('mineOnly', null);
								this.handlePaginationChange(1);
							};

							if (isProfileOpen) {
								return (
									<div className="job-positions-list">
										<div className="ats-table-wrapper">
											{showMineEmpty ? (
												<EmptyState
													size="sm"
													icon={EMPTY_STATE_ICONS.allCaughtUp}
													iconAlt="All caught up"
													message="All caught up"
													detail="You’re viewing only your pending tasks. Want to see all data?"
													actionText="Turn off Me mode"
													onActionClick={turnOffMine}
												/>
											) : (
												this.listStore.data.map(job => (
													<JobPositionCard
														key={job.id}
														job={job}
														onClick={this.openProfile}
														isActive={
															String(this.state.activeJobPositionId) ===
															String(job.id)
														}
													/>
												))
											)}
										</div>
									</div>
								);
							}

							return (
								<div className="job-positions-container">
									<div className="ats-table-wrapper">
										<JobPositionsTable
											data={this.listStore.data}
											sort={this.listStore.store.query.sort}
											onSort={this.listStore.handleSort}
											onSuccess={() => this.listStore.search()}
											onClickRow={this.openProfile}
											activeRowId={this.state.activeJobPositionId}
											visibleColumns={this.state.visibleColumns}
											onVisibleColumnsChange={cols =>
												this.setState({ visibleColumns: cols })
											}
											loading={this.listStore.loading}
											isEmpty={
												!this.listStore.loading &&
												this.listStore.data.length === 0
											}
											emptyState={this.renderJobPositionsEmptyState()}
										/>
									</div>
								</div>
							);
						})()}
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							totalResults={this.listStore.store.query.pagination.totalResults}
							onPaginationChange={this.handlePaginationChange}
						/>
					</div>
					<DrawerContainer id="job-positions" />
				</main>
			</div>
		);
	}
}
